<template>
  <ValidationObserver ref="observer">
    <v-form class="pa-4 text-left" v-if="transaction">
      <v-container>
        <h4>{{ formTitle }}</h4>

        <v-row>
          <v-col cols="12" sm="4">
            <ValidationProvider
              v-slot="{ errors }"
              name="Transaction Type"
              rules="required"
            >
              <v-select
                v-model="transaction.transactionType"
                label="Transaction Type"
                :items="bulkTransactionTypesForDropdown"
                item-text="name"
                item-value="key"
                :error-messages="errors"
                @change="onTypeSelected"
              ></v-select>
            </ValidationProvider> </v-col
          ><v-col cols="6" sm="4">
            <ValidationProvider
              v-slot="{ errors }"
              name="Tank"
              rules="required"
            >
              <v-select
                v-model="transaction.tank"
                label="Tank"
                :items="vesselTanks"
                item-text="tankName"
                return-object
                :error-messages="errors"
                @change="onTankSelected"
                :disabled="isCurrentStatusEdit"
              ></v-select>
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row v-if="selectedTank && selectedTankStatus">
          <v-col cols="12" md="8">
            <TankStatusOverviewCard
              v-if="selectedTank"
              :tank="selectedTank"
              :tankStatus="selectedTankStatus"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <ValidationProvider
              v-slot="{ errors }"
              name="Bulk"
              rules="required"
            >
              <v-select
                v-model="transaction.bulk"
                :items="bulkForSelectedTankType"
                item-text="bulkName"
                item-value="bulkName"
                label="Bulk"
                :error-messages="errors"
                :disabled="disableBulkField"
              ></v-select>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4">
            <ValidationProvider
              v-slot="{ errors }"
              name="Bulk Quantity"
              :rules="useQuantityValidationRules ? quantityValidationRule : ''"
            >
              <v-text-field
                type="number"
                step="0.1"
                v-model="transaction.quantity"
                :label="
                  selectedTank ? `Quantity (${selectedTankQtyUom})` : 'Quantity'
                "
                :error-messages="errors"
                :suffix="selectedTankQtyUom"
                :disabled="!selectedTank"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="4" class="py-0 ma-0">
            <v-dialog
              ref="completedDateMenu"
              v-model="completedDateMenu"
              transition="scale-transition"
              width="unset"
            >
              <template v-slot:activator="{ on }">
                <ValidationProvider
                  name="Completed Date"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    v-model="transaction.completedDate"
                    label="Completed Date"
                    readonly
                    v-on="on"
                    :error-messages="errors"
                  ></v-text-field>
                </ValidationProvider>
              </template>
              <v-date-picker
                v-model="transaction.completedDate"
                @change="completedDateSelected()"
                no-title
                scrollable
                :min="minDate"
                :max="maxDate"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="completedDateMenu = false"
                  >Cancel</v-btn
                >
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="6" md="4" class="py-0 ma-0">
            <v-dialog
              ref="completedTimeMenu"
              v-model="completedTimeMenu"
              transition="scale-transition"
              max-width="300"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="transaction.completedTime"
                  label="Completed Time"
                  readonly
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="completedTimeMenu"
                v-model="transaction.completedTime"
                format="24hr"
                full-width
                @click:minute="completedTimeMenu = false"
                :min="minTime"
                :max="maxTime"
              ></v-time-picker>
            </v-dialog>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4" class="py-0 ma-0">
            <ValidationProvider
              name="Bulk Delivery Note Number"
              rules="max:100"
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="transaction.bulkDeliveryNoteNumber"
                label="Bulk Delivery Note Number"
                :error-messages="errors"
                :counter="100"
              ></v-text-field>
            </ValidationProvider>
          </v-col>
          <v-col cols="8" md="4" class="py-0 ma-0">
            <ValidationProvider
              name="Bulk Delivery Note File"
              rules="file_type_pdf|file_size:10485760"
              v-slot="{ errors }"
            >
              <v-file-input
                v-model="transaction.bulkDeliveryNoteFile"
                placeholder="Bulk Delivery Note File"
                truncate-length="25"
                accept=".pdf"
                :error-messages="errors"
                :disabled="!isClientConnectedToServer"
                @change="onBulkDeliveryNoteChange()"
                :clearable="false"
              ></v-file-input>
            </ValidationProvider>
          </v-col>
          <v-col
            class="d-flex align-center justify-space-around"
            cols="4"
            md="2"
          >
            <v-icon
              :disabled="disableBulkNoteAttachmentComponents"
              @click="downloadBulkDeliveryNoteForItem(transaction.id, true)"
              >mdi-note</v-icon
            >
            <v-icon
              :disabled="disableBulkNoteAttachmentComponents"
              @click="downloadBulkDeliveryNoteForItem(transaction.id)"
              >mdi-download</v-icon
            >
            <v-icon
              :disabled="disableBulkNoteAttachmentComponents"
              @click="deleteBulkDeliveryNoteClicked"
              >mdi-delete</v-icon
            >
          </v-col>
        </v-row>
        <ValidationProvider name="Comments" rules="max:500" v-slot="{ errors }">
          <v-textarea
            v-model="transaction.comments"
            label="Comments"
            :error-messages="errors"
            :counter="500"
          ></v-textarea>
        </ValidationProvider>
        <v-btn color="primary" :loading="isSubmitting" @click="submit"
          >Submit</v-btn
        >
      </v-container>
    </v-form>
  </ValidationObserver>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import TankStatusOverviewCard from './TankStatusOverviewCard.vue';
import BulkTransactionsHelpersMixin from '../../mixins/BulkTransactionsHelpersMixin';
import '../../validators';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';

export default {
  name: 'BulkTransactionForm',
  mixins: [BulkTransactionsHelpersMixin],
  props: {
    transaction: Object,
    vesselId: String,
    onSubmitClick: Function,
    onUpdateAttachmentClick: {
      type: Function,
      required: false,
    },
    removeBulkDeliveryNote: {
      type: Function,
      required: false,
    },
    activity: {
      type: Object,
      required: true,
    },
  },
  components: {
    ValidationObserver,
    ValidationProvider,
    TankStatusOverviewCard,
  },
  data() {
    return {
      completedDateMenu: false,
      completedTimeMenu: false,
      disableTankUpdateCheckbox: false,
      isSubmitting: false,
      isFileSubmitting: false,
      title: 'Create Bulk Transaction',
      editTitle: 'Edit Bulk Transaction',
      vesselTanks: [],
    };
  },
  created() {
    this.$store.dispatch('bulksStore/loadBulks', this.getActiveSite.id);
    this.$store.dispatch('bulkTransactionsStore/loadBulkTransactionTypes');
    this.vesselTanks = this.vesselTanksWithStatus;
  },
  computed: {
    ...mapState(['bulkTransactionsStore', 'bulksStore']),
    ...mapGetters({
      getActiveSite: 'userStore/getActiveSite',
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getVesselTankStatuses: 'tanksStore/getVesselTanksWithCurrentStatus',
    }),
    formTitle() {
      return this.isEdit ? this.editTitle : this.title;
    },
    bulkTransactionTypes() {
      return this.bulkTransactionsStore ? this.bulkTransactionsStore.bulkTransactionTypes : null;
    },
    bulks() {
      return this.bulksStore ? this.bulksStore.bulks : null;
    },
    unitOfMeasurements() {
      return this.bulkTransactionsStore ? this.bulkTransactionsStore.unitOfMeasurements : null;
    },
    bulkTransactionTypesForDropdown() {
      return this.bulkTransactionTypes
        .filter((x) => x.name.toLowerCase() !== 'notset');
    },
    vesselTanksWithStatus() {
      var tanks = this.getVesselTankStatuses(this.vesselId);
      if (!this.transaction.transactionType || (this.transaction.transactionType.toLowerCase() !== 'dumped' && this.transaction.transactionType.toLowerCase() !== 'generated')) {
        return tanks;
      }
      return tanks.filter(x => x.tankBulkType.toLowerCase() === 'water');
    },
    selectedTank() {
      let result = this.transaction.tank ?? null;

      if (this.isEdit && this.transaction.tank) {
        const { id } = this.transaction.tank;
        result = this.vesselTanksWithStatus.find((x) => x.id === id);
      }

      return result;
    },
    selectedTankStatus() {
      const result = this.selectedTank ? this.selectedTank.currentTankStatus : null;
      return result;
    },
    selectedTankQtyUom() {
      return this.selectedTank ? this.selectedTank.qtyUom : '';
    },
    bulkForSelectedTankType() {
      return this.selectedTank
        ? this.bulks.filter((x) => x.bulkType === this.selectedTank.tankBulkType)
        : null;
    },
    isEdit() {
      return !!this.transaction.id || !!this.transaction.idc;
    },
    bulkDeliveryNoteFile() {
      return this.transaction.bulkDeliveryNoteFile;
    },
    validQuantityValidationRule() {
      return {
        required: true,
        validQuantity: {
          maxCapacity: this.selectedTank.maxCapacity,
          qtyUom: this.selectedTank.qtyUom,
        },
      };
    },
    loadingQuantityValidationRules() {
      if (this.selectedTank && this.transaction.isTankUpdate) {
        return {
          required: true,
          validLoadingQuantity: {
            maxCapacity: this.selectedTank.maxCapacity,
            currentQuantity: this.selectedTankStatus.quantityOnBoard,
            qtyUom: this.selectedTank.qtyUom,
          },
        };
      }
      return this.validQuantityValidationRule;
    },
    deliveryQuantityValidationRules() {
      if (this.selectedTank && this.transaction.isTankUpdate) {
        return {
          required: true,
          validDeliveryQuantity: {
            currentQuantity: this.selectedTankStatus.quantityOnBoard,
            qtyUom: this.selectedTank.qtyUom,
          },
        };
      }
      return this.validQuantityValidationRule;
    },
    quantityValidationRule() {
      switch (this.transaction.transactionType) {
        case 'Loaded':
        case 'Generated':
          return this.loadingQuantityValidationRules;
        case 'Delivery':
        case 'Consumption':
        case 'Dumped':
          return this.deliveryQuantityValidationRules;
        default:
          return this.validQuantityValidationRule;
      }
    },
    disableBulkField() {
      if (!this.selectedTank) return true;

      const currentTankQuantity = this.selectedTankStatus
        ? parseFloat(this.selectedTankStatus.quantityOnBoard)
        : null;

      const isTankEmpty = !currentTankQuantity || currentTankQuantity <= 0;
      return !isTankEmpty || (this.isEdit && this.isCurrentStatusEdit);
    },
    isUpdateTankStatusDisabled() {
      //return !!this.transaction.id
      //  || this.disableTankUpdateCheckbox
      //  || !this.selectedTankStatus;

      return false;
    },
    useQuantityValidationRules() {
      return this.selectedTank !== null && this.selectedTankStatus !== null;
    },
    completedTime() {
      return this.transaction.completedTime;
    },
    disableBulkNoteAttachmentComponents() {
      return !this.transaction.id
        || !this.isClientConnectedToServer
        || !this.transaction.bulkDeliveryNoteFile;
    },
    minDate() {
      return dateTimeHelpers.getDateStringFromDateTime(this.activity.startDateTimeLt);
    },
    minTime() {
      let result = null;
      if (this.activity.startDateTimeLt && this.transaction.completedDate === this.minDate) {
        result = dateTimeHelpers.getTimeStringFromDateTime(this.activity.startDateTimeLt);
      }
      return result;
    },
    maxDate() {
      let result = null;
      if (this.activity.endDateTimeLt) {
        result = dateTimeHelpers.getDateStringFromDateTime(this.activity.endDateTimeLt);
      }
      return result;
    },
    maxTime() {
      let result = null;
      if (this.activity.endDateTimeLt && this.transaction.completedDate === this.maxDate) {
        result = dateTimeHelpers.getTimeStringFromDateTime(this.activity.endDateTimeLt);
      }
      return result;
    },
    isCurrentStatusEdit() {
      return this.isEdit;

      //let isCurrentStatusEdit = false;
      // If the current tank status is linked to this transaction
      //if (this.isEdit && this.selectedTankStatus) {
      //  isCurrentStatusEdit = this.selectedTankStatus.bulkTransactionId === this.transaction.id;
      //}

      //return isCurrentStatusEdit;
    },
  },
  watch: {
    selectedTankStatus(newVal) {
      this.transaction.currentSelectedTankStatus = newVal;
    },
    completedTime(newVal) {
      // Disable Tank update if completedDateTimeLt is before current tank status recorded time
      //if (newVal && this.transaction.completedDate && this.selectedTankStatus) {
      //  const currentTankStatusDateTimeLt = this.selectedTankStatus.statusDateTimeLt;
      //  const completedDateTimeLt = this.getCompletedDateTime(this.transaction);

      //  if (completedDateTimeLt < currentTankStatusDateTimeLt) {
      //    this.disableTankUpdateCheckbox = true;
      //  } else {
      //    this.disableTankUpdateCheckbox = false;
      //  }
      //}
    },
    isUpdateTankStatusDisabled(newVal) {
      // set tank update to false if field is disabled
      //this.transaction.isTankUpdate = !newVal;
    },
  },
  methods: {
    submit() {
      this.$refs.observer.validate()
        .then((isFormValid) => {
          if (isFormValid) {
            this.isSubmitting = true;
            this.onSubmitClick()
              .finally(() => { this.isSubmitting = false; });
          }
        });
    },
    submitFile(transaction) {
      this.$refs.observer.validate()
        .then((isFormValid) => {
          if (isFormValid) {
            this.isFileSubmitting = true;
            this.onUpdateAttachmentClick(transaction)
              .finally(() => { this.isFileSubmitting = false; });
          }
        });
    },
    resetForm() {
      this.$refs.observer.reset();
    },
    completedDateSelected() {
      this.completedDateMenu = false;
      this.completedTimeMenu = true;
    },
    onTankSelected() {
      const { tank } = this.transaction;
      this.transaction.bulk = this.selectedTankStatus.bulk;
    },
    onTypeSelected() {
      this.vesselTanks = this.vesselTanksWithStatus;
    },
    onBulkDeliveryNoteChange() {
      this.$nextTick(() => {
        if (this.bulkDeliveryNoteFile && this.isEdit) {
          this.submitFile(this.transaction);
        }
      });
    },
    deleteBulkDeliveryNoteClicked() {
      if (this.bulkDeliveryNoteFile && this.isEdit) {
        this.isFileSubmitting = true;
        this.$nextTick(() => {
          this.removeBulkDeliveryNote(this.transaction.id)
            .finally(() => { this.isFileSubmitting = false; });
        });
      }
    },
  },
};
</script>
